import PopupBasic from 'src/game/components/popups/PopupBasic';
import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import View from '@play-co/timestep-core/lib/ui/View';
import sounds from 'src/lib/sounds';
import { Feature } from 'src/replicant/analytics';
import {
  ImageScaleView,
  ImageView,
  LinearAddFilter,
} from '@play-co/timestep-core/ui';
import { Match2Timer } from '../../components/Match2Timer';
import {
  FlexBoxImageScaleView,
  FlexBoxView,
} from '@play-co/timestep-core/flexbox';
import { AsyncScroll } from 'src/game/components/shared/AsyncScroll';
import TeamNav from '../components/TeamNav';
import { getCurrentTeamCompetition } from '../team.sequence';
import {
  TeamEventData,
  TeamCompetitor,
} from 'src/replicant/team/competition/teamCompetition.ruleset';
import { TeamCompetitorEntry } from '../components/TeamCompetitorEntry';
import StateObserver from 'src/StateObserver';
import { getCurrentTeamEventEndDate } from 'src/replicant/team/team.getters';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { Match2RewardItem } from '../../components/Match2RewardItem';
import gsap from 'gsap';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import ButtonScaleView from 'src/lib/ui/components/ButtonScaleView';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';

import { AssetGroup } from '@play-co/timestep-core';

const ui = uiConfig.buruburu.care.confirmation;

export default class PopupTeamCompetitionLeaderboard extends PopupBasic {
  private header: { update: (data?: TeamEventData) => void };
  private members: { update: (data?: TeamEventData) => void };
  private rewardItem?: Match2RewardItem;

  constructor(opts: { superview: View; close: () => void }) {
    super({
      superview: opts.superview,
      close: () => {
        opts.close();
      },
      ...ui.popup,
      width: 692,
      height: 1100,
      skipBackground: true,
      skipMessage: true,
      darkerBg: true,
      tracking: {
        feature: Feature.MATCH2.TEAM._,
        $subFeature: Feature.MATCH2.TEAM.INFO_POPUP._,
      },
    });

    this.title.root.removeFromSuperview();
    this.buttonClose.updateOpts({ width: 72, height: 76, x: 650, y: 0 });

    this.header = this.createHeader(this.box);
    this.members = this.createMembers(this.box);
  }

  protected getAssetsGroup() {
    return new AssetGroup([
      'assets/ui/match2/team/competition/comp_title_bg.png',
      'assets/ui/match2/team/competition/comp_header_bg.png',
      'assets/ui/match2/team/team_awning.png',
      'assets/ui/match2/team/competition/btn_info.png',
      'assets/ui/match2/team/competition/comp_title_en.png',
      'assets/ui/match2/team/competition/comp_title_ja.png',
      'assets/ui/match2/team/competition/comp_header_active.png',
      'assets/ui/match2/common/banner_red.png',
      'assets/ui/match2/team/competition/panel_team_competition_rank.png',
      'assets/ui/match2/team/competition/icon_teamCompetition_sm.png',
      'assets/ui/match2/team/competition/comp_panel_reward.png',
      'assets/ui/match2/common/fx_shine.png',
      'assets/ui/match2/team/frames/panel_list_beige_inset.png',
    ]);
  }

  private createHeader(superview: View) {
    const size = { width: 646, height: 318 };
    const awningSize = { width: 692, height: 106 };

    // Title background pattern.
    new ImageView({
      superview,
      width: 554,
      height: 156,
      image: `assets/ui/match2/team/competition/comp_title_bg.png`,
      centerOnOrigin: true,
      x: awningSize.width * 0.5,
      y: -20,
    });

    // Header image.
    const header = new ImageScaleView({
      superview,
      width: size.width,
      height: size.height,
      x: (superview.style.width - size.width) * 0.5,
      y: 65,
      image: 'assets/ui/match2/team/competition/comp_header_bg.png',
    });

    // Header timer.
    const timer = new Match2Timer({
      superview: header,
      x: header.style.width * 0.5,
      y: header.style.height,
      centerOnOrigin: true,
    });

    // Header awning.
    const awning = new ImageView({
      superview,
      image: 'assets/ui/match2/team/team_awning.png',
      width: awningSize.width,
      height: awningSize.height,
      offsetX: -(superview.style.width - size.width) * 0.5,
      x: (superview.style.width - size.width) * 0.5,
    });

    new ButtonScaleView({
      superview,
      centerOnOrigin: true,
      image: 'assets/ui/match2/team/competition/btn_info.png',
      width: 56,
      height: 63,
      x: 50,
      tracking: {
        feature: Feature.MATCH2.TEAM.COMPETITION._,
        $subFeature: Feature.MATCH2.TEAM.COMPETITION.INFO_BUTTON,
      },
      onClick: () => openPopupPromise('popupTeamCompetitionInfo', {}),
    });

    // Title.
    if (StateObserver.getLocale() === 'en') {
      new ImageView({
        superview,
        width: 450,
        height: 109,
        image: `assets/ui/match2/team/competition/comp_title_en.png`,
        centerOnOrigin: true,
        x: awningSize.width * 0.5,
        y: -20,
      });
    } else {
      new ImageView({
        superview,
        width: 318,
        height: 148,
        image: `assets/ui/match2/team/competition/comp_title_ja.png`,
        centerOnOrigin: true,
        x: awningSize.width * 0.5,
        y: -20,
      });
    }

    return {
      update: (data?: TeamEventData) => {
        // Active event.
        if (!data) {
          timer.setRemainingTimeGetter(
            (now) => (getCurrentTeamEventEndDate(now) ?? 0) - now,
          );
          timer.autoUpdate();

          // Create hero image.
          new ImageView({
            superview: header,
            width: 610,
            height: 246,
            x: header.style.width * 0.5,
            y: header.style.height * 0.5,
            image: 'assets/ui/match2/team/competition/comp_header_active.png',
            centerOnOrigin: true,
          });
          return;
        }

        // Inactive event.
        this.buttonClose.removeFromSuperview();
        timer.setRemainingTimeGetter(() => 0);
        timer.update();

        const ribbon = new ImageScaleView({
          superview: awning,
          width: 230,
          height: 60,
          centerOnOrigin: true,
          x: awning.style.width * 0.5,
          y: awning.style.height - 20,
          image: 'assets/ui/match2/common/banner_red.png',
        });

        new LangBitmapFontTextView({
          superview: ribbon,
          width: ribbon.style.width,
          height: ribbon.style.height,
          y: -5,
          font: 'Body',
          localeText: () => i18n('match2.team.results'),
          size: 40,
          align: 'center',
          verticalAlign: 'center',
        });

        // Create rank section.
        const rankSection = new View({
          superview: header,
          width: 219,
          height: 104,
          centerOnOrigin: true,
          x: header.style.width / 4,
          y: 5 + header.style.height * 0.5,
        });

        // Team rank title.
        new LangBitmapFontTextView({
          superview: rankSection,
          width: 200,
          height: 40,
          font: 'Body',
          localeText: () => i18n('match2.team.team_rank'),
          size: 26,
          align: 'center',
          verticalAlign: 'center',
          x: rankSection.style.width * 0.5,
          y: -20,
          centerOnOrigin: true,
          color: '#662420',
        });

        // Create frame.
        const frame = new ImageScaleView({
          superview: rankSection,
          image:
            'assets/ui/match2/team/competition/panel_team_competition_rank.png',
          width: 206,
          height: 67,
          scaleMethod: '9slice',
          sourceSlices: {
            horizontal: { left: 30, right: 30 },
            vertical: { top: 30, bottom: 30 },
          },
          centerOnOrigin: true,
          x: rankSection.style.width * 0.5,
          y: rankSection.style.height * 0.5,
        });

        // Rank label.
        new LangBitmapFontTextView({
          superview: frame,
          width: frame.style.width,
          height: frame.style.height,
          font: 'Body',
          localeText: () =>
            `#${data.teams.find((x) => x.current)?.position.toString() ?? ''}`,
          size: 40,
          align: 'center',
          verticalAlign: 'center',
          x: 30,
          color: '#F24463',
        });

        // Add icon.
        new ImageView({
          superview: rankSection,
          image:
            'assets/ui/match2/team/competition/icon_teamCompetition_sm.png',
          width: 105,
          height: 105,
          centerOnOrigin: true,
          x: 30,
          y: rankSection.style.height * 0.5,
        });

        // Add reward frame.
        const rewardFrame = new ImageView({
          superview: header,
          image: 'assets/ui/match2/team/competition/comp_panel_reward.png',
          width: 144,
          height: 134,
          centerOnOrigin: true,
          x: 30 + (3 * header.style.width) / 4,
          y: 5 + header.style.height * 0.5,
        });

        // Your rewards title.
        new LangBitmapFontTextView({
          superview: rewardFrame,
          width: 200,
          height: 40,
          font: 'Body',
          localeText: () => i18n('match2.team.your_reward'),
          size: 26,
          align: 'center',
          verticalAlign: 'center',
          x: rewardFrame.style.width * 0.5,
          y: -10,
          centerOnOrigin: true,
          color: '#F24463',
        });

        // Reward icon.
        if (!data.rewards) {
          new LangBitmapFontTextView({
            superview: rewardFrame,
            width: rewardFrame.style.width,
            height: rewardFrame.style.height,
            font: 'Body',
            localeText: () => i18n('match2.team.no_contribution'),
            size: 20,
            align: 'center',
            verticalAlign: 'center',
            wordWrap: true,
            x: rewardFrame.style.width * 0.5,
            y: rewardFrame.style.height * 0.5,
            centerOnOrigin: true,
            color: '#76233C',
          });
          return;
        }

        // Add glow.
        const glow = new ImageScaleView({
          superview: rewardFrame,
          image: 'assets/ui/match2/common/fx_shine.png',
          centerOnOrigin: true,
          centerAnchor: true,
          width: rewardFrame.style.width * 1.5,
          height: rewardFrame.style.height * 1.5,
          x: rewardFrame.style.width * 0.5,
          y: rewardFrame.style.height * 0.5,
          filter: new LinearAddFilter('#FFFFFF'),
          opacity: 0.15,
        });

        // Animate glow.
        gsap.to(glow.style, {
          duration: 13,
          r: Math.PI * 2,
          ease: 'none',
          repeat: -1,
        });

        const icon = new Match2RewardItem({
          superview: rewardFrame,
          size: 70,
          centerOnOrigin: true,
          x: rewardFrame.style.width * 0.5,
          y: rewardFrame.style.height * 0.5 - 10,
        });

        this.rewardItem = icon;
        icon.setReward(data.rewards[0]);
      },
    };
  }

  private createMembers(superview: View) {
    const { width, height } = { width: 646, height: 623 };

    const layout = new FlexBoxView({
      tag: 'membersListTabs',
      superview,
      width,
      height,
      x: (superview.style.width - width) * 0.5,
      y: 430,
      flex: {
        alignItems: 'center',
        flexDirection: 'column',
      },
    });

    const tabs = new TeamNav({
      superview: layout,
      zIndex: 1,
      height: 90,
    });

    const content = new FlexBoxImageScaleView({
      tag: 'membersList',
      ...uiConfig.popups.buruburu,
      superview: layout,
      width: width,
      height: height - tabs.style.height,
      flex: {
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        marginTop: -7,
      },
    });

    const bg = new ImageScaleView({
      tag: 'membersListBg',
      superview: content,
      image: 'assets/ui/match2/team/frames/panel_list_beige_inset.png',
      scaleMethod: '9slice',
      sourceSlices: {
        horizontal: { left: 39, right: 39 },
        vertical: { top: 39, bottom: 39 },
      },
      width: 590,
      height: content.style.height - 35,
    });

    const scroll = new AsyncScroll<TeamCompetitor, TeamCompetitorEntry>({
      superview: bg,
      rect: {
        x: 0,
        y: 0,
        width: bg.style.width,
        height: bg.style.height,
      },
      margin: 2,
      createItem: (superview, _, data) => {
        const entry = new TeamCompetitorEntry({
          superview,
          width: bg.style.width * 0.98,
          height: 90,
        });
        entry.style.x = (superview.style.width - entry.style.width) * 0.5;
        if ('loading' in data) return entry;
        entry.setup(data);
        return entry;
      },
      recycle: true,
      setData: (_, data, view) => {
        if ('loading' in data) return;
        view.setup(data);
      },
    });

    const stickyView = new TeamCompetitorEntry({
      x: 0,
      width: bg.style.width,
      height: 95,
    });

    stickyView.updateOpts({
      height: stickyView.style.height - 20,
      offsetY: -10,
    });

    return {
      update: (data?: TeamEventData) => {
        const dataProvider = () =>
          data ? Promise.resolve(data) : getCurrentTeamCompetition()?.get();

        tabs.setup({
          tabs: [
            {
              label: 'match2.team.tab_competition',
              onClick: () => {
                scroll.clearStickyItem();
                scroll.setDataProvider(
                  async () => {
                    const competition = await dataProvider();
                    if (!competition) return [];
                    const team = competition.teams.find(
                      (player) => player.current,
                    );
                    if (team) {
                      scroll.setStickyItem(stickyView, team.position - 1);
                      stickyView.setup({ ...team, alt: true });
                    }
                    return competition.teams;
                  },
                  {
                    feature: Feature.TEAM._,
                    subFeature: Feature.TEAM.TAB_COMPETITION,
                  },
                );
              },
              tracking: {
                feature: Feature.MATCH2.TEAM._,
                $subFeature: Feature.MATCH2.TEAM.COMPETITION.TAB_COMPETITION,
              },
            },
            {
              label: 'match2.team.tab_my_team',
              onClick: () => {
                scroll.clearStickyItem();
                scroll.setDataProvider(
                  async () => {
                    const competition = await dataProvider();
                    if (!competition) return [];
                    const player = competition.myTeam.find(
                      (player) => player.current,
                    );
                    if (player) {
                      scroll.setStickyItem(stickyView, player.position - 1);
                      stickyView.setup({ ...player, alt: true });
                    }
                    return competition.myTeam;
                  },
                  {
                    feature: Feature.TEAM._,
                    subFeature: Feature.TEAM.TAB_MY_TEAM,
                  },
                );
              },
              tracking: {
                feature: Feature.MATCH2.TEAM._,
                $subFeature: Feature.MATCH2.TEAM.COMPETITION.TAB_MY_TEAM,
              },
            },
          ],
          tabOpts: {
            width: layout.style.width / 2 - 10,
            height: 90,
          },
        });

        if (data) {
          new ButtonScaleViewWithText({
            superview,
            ...uiConfig.buttons.secondary,
            centerOnOrigin: true,
            x: superview.style.width * 0.5,
            y: superview.style.height - 40,
            fontSize: 40,
            width: 235,
            height: 90,
            font: 'Body',
            localeText: () =>
              i18n('match2.tournament.claim').toLocaleUpperCase(),
            tracking: {
              feature: Feature.DECORATION._,
              $subFeature: Feature.DECORATION.SAVE,
            },
            onClick: async () => {
              sounds.sfx.play('ok');
              await this.rewardItem?.grantReward(this.root);
              this.opts.close();
            },
          });
        }
      },
    };
  }

  public init({ data }: { data?: TeamEventData }): void {
    super.init({
      title: i18n('match2.team.info_popup.title'),
    });

    this.header.update(data);
    this.members.update(data);
  }
}
